<template>
  <div class="debug">
    <div class="debug-map">
      <l-map :zoom="16" :center="center">
        <l-tile-layer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy;OpenStreetMap contributors"
        />
        <l-circle-marker
          v-for="marker in markers"
          :lat-lng="marker.position"
          :radius="5"
          :color="marker.color"
          @click="markerClicked"
          :key="marker.key"
        >
          <l-tooltip :options="{ permanent: false, interactive: true }">
            {{ marker.label }}
          </l-tooltip>
        </l-circle-marker>
      </l-map>
    </div>
    <NearestShop :position="showShopForPosition" />

    <h2 class="title mt-5">Performance Measurements</h2>
    <table class="table">
      <th></th>
      <th>Name</th>
      <th>Duration</th>
      <tr
        v-for="(measurement, index) in performanceMeasurements"
        :key="measurement.name + measurement.duration + index"
      >
        <td>{{ index + 1 }}.</td>
        <td>{{ measurement.name }}</td>
        <td>{{ measurement.duration }} ms</td>
      </tr>
    </table>

    <h2 class="title mt-5">Demo States</h2>
    <button class="button load-demo-state-1" @click="loadDemoState(0)">
      Load #1
    </button>
  </div>
</template>
<script>
import Vue from "vue";
// If you need to reference 'L', such as in 'L.icon', then be sure to
// explicitly import 'leaflet' into your component
import L from "leaflet";
import { latLng } from "leaflet";
import { LMap, LTileLayer, LCircleMarker, LTooltip } from "vue2-leaflet";
import "leaflet/dist/leaflet.css";

import { Shop } from "@/models/Shop.ts";
import { ShoppingList } from "@/models/ShoppingList.ts";
import { ManualShoppingListEntry } from "@/models/ShoppingListEntry";
import SmartOrderShoppingList from "@/models/SmartOrderShoppingList";
import NearestShop from "@/components/NearestShop.vue";

// https://vue2-leaflet.netlify.app/quickstart/#marker-icons-are-missing
import { Icon } from "leaflet";
delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const edekaEntries = [
  new ManualShoppingListEntry("Salz"),
  new ManualShoppingListEntry("Butter"),
  new ManualShoppingListEntry("1kg Weizenmehl"),
].map((entry) => {
  entry.commonShoppingPosition = {
    latitude: 50,
    longitude: 10,
  };
  return entry;
});

const rossmannEntries = [
  new ManualShoppingListEntry("Zahnpasta"),
  new ManualShoppingListEntry("Glasreiniger"),
].map((entry) => {
  entry.commonShoppingPosition = {
    latitude: 50,
    longitude: 11,
  };
  return entry;
});

const demoState1 = {
  shops: [
    new Shop({ name: "Edeka", position: { latitude: 50, longitude: 10 } }),
    new Shop({ name: "Rossmann", position: { latitude: 50, longitude: 11 } }),
  ],
  shoppingList: {
    entries: [...edekaEntries, ...rossmannEntries],
  },
};

const demoStates = [demoState1];

export default Vue.extend({
  name: "Debug",
  components: {
    LMap,
    LTileLayer,
    LCircleMarker,
    LTooltip,
    NearestShop,
  },
  data() {
    return {
      showShopForPosition: {
        latitude: 0,
        longitude: 0,
      },
    };
  },
  methods: {
    markerClicked(target) {
      this.showShopForPosition = {
        latitude: target.latlng.lat,
        longitude: target.latlng.lng,
      };
    },
    loadDemoState(demoStateIndex) {
      this.$store.commit("loadDemoState", demoStates[demoStateIndex]);
    },
  },
  computed: {
    center() {
      if (this.markers.length === 0) {
        return latLng(55, 7);
      }

      return this.markers[0].position;
    },
    markers() {
      const vibrantColors = [
        "#01ff1f",
        "#bd00ff",
        "#ff9a00",
        "#0078ff",
        "#e3ff00",
      ];
      let markers = [];

      this.smartClusteredEntries.forEach((cluster, index) => {
        cluster.entries.forEach((entry) => {
          if (entry.commonShoppingPosition) {
            markers.push({
              position: latLng(
                entry.commonShoppingPosition.latitude,
                entry.commonShoppingPosition.longitude
              ),
              label: entry.ingredient,
              color: vibrantColors[index],
              key: entry._id,
            });
          }
        });

        const centroidPosition = cluster.centroidPosition();
        if (centroidPosition) {
          markers.push({
            position: latLng(
              centroidPosition.latitude,
              centroidPosition.longitude
            ),
            label: "Centroid",
            color: "#444444",
            key: `centroid-${centroidPosition.latitude}-${centroidPosition.longitude}`,
          });
        }
      });

      this.shops.forEach((shop) => {
        markers.push({
          position: latLng(shop.position.latitude, shop.position.longitude),
          label: shop.name,
          color: "#000000",
          key: shop._id,
        });
      });
      return markers;
    },
    entries: function () {
      return this.$store.state.shoppingList.entries;
    },
    currentEntries: function () {
      const ONE_HOUR = 60 * 60 * 1000;
      return this.entries.filter((entry) => {
        return (
          (entry.shoppedAt === null && entry.isShopped === false) ||
          (entry.shoppedAt && new Date() - entry.shoppedAt < ONE_HOUR * 24)
        );
      });
    },
    smartClusteredEntries: function () {
      const list = new SmartOrderShoppingList(this.currentEntries);
      return list.clustered();
    },
    shops: function () {
      return this.$store.state.shops;
    },
    performanceMeasurements() {
      return this.$store.state.performanceMeasurementsFinished;
    },
  },
});
</script>
<style>
.debug-map {
  height: 40em;
  width: 100%;
}
</style>
